import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Messages } from 'primereact/messages';
import { getFormErrorMessage, maxLengthMessage } from '../../../utils/errors';
import { RHFInputText, RHFDropdown } from '../../../components/rhf';
import { ModalDialog } from '../../../components/ModalDialog';
import { LineDivider } from '../../../components/LineDivider';
import { ButtonAdd, ButtonDelete, ButtonEdit } from '../../../components/Buttons';
import { FormContainer, FormInput, FormRow, Label } from '../../../components/form';
import { InputText } from '../../../../components/PrimereactComponents';
import { DATABAZE } from '../../../constants/field_lengths';
import { PrimeIcons } from 'primereact/api';


export const DatabaseDialog = forwardRef(({
    isVisible,
    onHide,
    onSubmitAdd,
    onSubmitEdit,
    onSubmitDelete,
    databaseTypeOptions,
    editDatabase,
    msgs
}, ref) => {
    const [processing, setProcessing] = useState(false);

    useImperativeHandle(ref, () => ({
        reset: reset
    }));

    const formId = 'manage-database-dialog';

    const edit = !!editDatabase;
    const onSubmit = edit ? onSubmitEdit : onSubmitAdd;

    const defaultValues = useMemo(
        () => ({
            db_type: null,
            name: '',
            name_confirmation: ''
        }), []
    );

    const {
        control,
        formState: { errors, isSubmitting },
        handleSubmit,
        getValues,
        reset
    } = useForm({
        disabled: processing,
        defaultValues: defaultValues
    });

    useEffect(() => {
        setProcessing(isSubmitting);
    }, [isSubmitting]);

    useEffect(() => {
        if (editDatabase) {
            reset({
                db_type: editDatabase.db_type,
                name: editDatabase.name,
                name_confirmation: ''
            });
        } else {
            reset(defaultValues);
        }
    }, [editDatabase, reset, defaultValues]);

    useEffect(() => {
        if (!isVisible) {
            msgs?.current?.clear();
        }
    }, [isVisible, msgs]);

    const handleDelete = () => {
        const nameConfirmation = getValues('name_confirmation');
        onSubmitDelete(nameConfirmation);
    };

    return(
        <ModalDialog
            visible={isVisible}
            header={edit ? `Upravit databázi ${editDatabase.name}` : `Vytvořit databázi`}
            style={{ width: 500 }}
            footer={
                edit ? (
                    <div className='flex-space-between'>
                        <ButtonDelete label='Smazat' type='button' form={formId} onClick={handleDelete} />
                        <ButtonEdit label='Upravit' type='submit' form={formId} />
                    </div>
                ) : (
                    <ButtonAdd label='Vytvořit' type='submit' form={formId} />
                )
            }
            onHide={onHide}
        >
            <Messages ref={msgs} className='flex-center' />

            <form onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormContainer type='dialog'>
                    <FormRow>
                        <FormInput>
                            <Label htmlFor='db_type' text='Typ databáze' required />
                            {edit ? (
                                <InputText name='db_type' disabled defaultValue={editDatabase?.type_name} />
                            ) : (
                                <RHFDropdown
                                    name='db_type'
                                    control={control}
                                    rules={{ required: 'Vyplňte toto pole' }}
                                    placeholder='Zvolte typ databáze'
                                    inputProps={{ options: databaseTypeOptions, showClear: false }}
                                />
                            )}
                            {getFormErrorMessage(errors, 'db_type')}
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput width='lg'>
                            <Label htmlFor='name' text='Název' required />
                            <RHFInputText
                                control={control}
                                name='name'
                                rules={{
                                    required: 'Vyplňte toto pole',
                                    maxLength: {
                                        value: DATABAZE.NAZEV,
                                        message: maxLengthMessage(DATABAZE.NAZEV)
                                    }
                                }}
                                placeholder='např. zastupitelstvo_brno'
                            />
                            {getFormErrorMessage(errors, 'body_name')}
                        </FormInput>
                    </FormRow>
                    {edit && (
                        <>
                            <LineDivider />
                            <i className={PrimeIcons.EXCLAMATION_TRIANGLE} />
                            Pro smazání vyplňte jméno databáze
                            <FormInput width='lg'>
                                <RHFInputText
                                    control={control}
                                    name='name_confirmation'
                                />
                                {getFormErrorMessage(errors, 'name_confirmation')}
                            </FormInput>
                        </>
                    )}
                </FormContainer>
            </form>

        </ModalDialog>
    );

});
